import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  ORDER_STATUSES,
  REFUND_STATUSES,
  type LedgerOrderItem,
  type LedgerSummary,
  type OrderRefundResponse,
  type SoldInventoryItem,
} from '@cuito/types';
import { Button } from '@cuito/ui';
import { EMPTY_LEDGER_FILTERS, ledgerApi, ledgerQueryOf, type LedgerFilterInput } from '../api/ledger';
import type { Paginated } from '../api/catalog';
import { locationsApi } from '../api/locations';
import type { LocationView } from '../api/stock';
import { ORDER_STATUS_TEXT } from '../cashier/components/OrderCard';
import LedgerSummaryCards from '../components/ledger/LedgerSummaryCards';
import OrderLedgerTable from '../components/ledger/OrderLedgerTable';
import RefundDialog from '../components/ledger/RefundDialog';
import SoldInventoryTab from '../components/ledger/SoldInventoryTab';
import { REFUND_STATUS_LABELS } from '../components/ledger/refundLabels';
import { readServerMessage } from '../lib/apiError';
import { saveBlob } from '../lib/saveBlob';
import { AMOUNT_UNAVAILABLE_TEXT, formatServerYuan } from '../lib/schemeForm';
import { useAuthStore } from '../stores/authStore';

/** 契约 §1.3：`pageSize` 默认 20 */
const PAGE_SIZE = 20;

function tabClass(selected: boolean): string {
  return `rounded-lg border-2 border-black px-4 py-2 text-sm font-bold ${
    selected ? 'bg-black text-white' : 'bg-white text-black'
  }`;
}

/**
 * 账本页（契约 §13.1 ~ §13.5；UI 规范 §3.2 `/ledger`）。
 *
 * 三件必须坚持的事：
 *   ① **`locationId` 可选**（§13.1：不传 = 本社团全部地点）⇒ 页面不要求先选地点，
 *      空态是「暂无账单记录」而不是「请先选择地点」（旧实现的空态与本契约冲突，见草稿「待裁决」4）；
 *   ② 汇总 / 售出库存 / 导出都用**同一份筛选**（不含分页：导出是全量的，§13.5）；
 *   ③ 退款成功、备注保存、导出成功都只回写**服务端下发的值**，页面不做金额减法。
 *
 * 退款入口（Task 12 接线）：`ledger:refund` 决定传不传 `onRefund`（服务端仍是真拦截），
 * 弹窗由 `key={`${orderId}:${refundNonce}`}` **重挂载** —— 每次打开都是新的一次提交
 * （`RefundDialog` 在挂载时生成幂等键），而同一次打开内的重试复用同一把键。
 * 通知分两支：首次 `已退款 <金额>`、同键重放 `退款已处理，未重复扣减：<金额>`（§13.6 重放零写入）。
 */
export default function LedgerPage() {
  const canRefund = useAuthStore((s) => s.hasPermission('ledger:refund'));

  const [filters, setFilters] = useState<LedgerFilterInput>(EMPTY_LEDGER_FILTERS);
  const [keywordDraft, setKeywordDraft] = useState('');
  const [page, setPage] = useState(1);

  const [orders, setOrders] = useState<Paginated<LedgerOrderItem> | null>(null);
  const [summary, setSummary] = useState<LedgerSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<'orders' | 'sold'>('orders');
  const [sold, setSold] = useState<SoldInventoryItem[]>([]);
  const [soldLoading, setSoldLoading] = useState(false);
  const [soldError, setSoldError] = useState<string | null>(null);

  const [locations, setLocations] = useState<LocationView[]>([]);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** 退款弹窗的目标订单；`refundNonce` 只用来换 `key` ⇒ **每次打开都是一次新的提交（新幂等键）** */
  const [refundTarget, setRefundTarget] = useState<LedgerOrderItem | null>(null);
  const [refundNonce, setRefundNonce] = useState(0);

  const query = useMemo(() => ledgerQueryOf(filters), [filters]);

  /** 单调递增的请求序号：先发的请求后到时**丢弃**它的响应（否则页面会跳回上一次筛选的结果） */
  const ordersSeq = useRef(0);
  const soldSeq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    ordersSeq.current += 1;
    const token = ordersSeq.current;
    setLoading(true);
    setError(null);
    try {
      const [orderPage, summaryRow] = await Promise.all([
        ledgerApi.listOrders({ ...query, page, pageSize: PAGE_SIZE }),
        ledgerApi.summary(query),
      ]);
      if (token !== ordersSeq.current) return;
      setOrders(orderPage);
      setSummary(summaryRow);
    } catch (e) {
      if (token !== ordersSeq.current) return;
      setError(readServerMessage(e) ?? '加载失败，请重试');
    } finally {
      if (token === ordersSeq.current) setLoading(false);
    }
  }, [page, query]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadSold = useCallback(async (): Promise<void> => {
    soldSeq.current += 1;
    const token = soldSeq.current;
    setSoldLoading(true);
    setSoldError(null);
    try {
      const rows = await ledgerApi.soldInventory(query);
      if (token !== soldSeq.current) return;
      setSold(rows);
    } catch (e) {
      if (token !== soldSeq.current) return;
      setSoldError(readServerMessage(e) ?? '加载失败，请重试');
    } finally {
      if (token === soldSeq.current) setSoldLoading(false);
    }
  }, [query]);

  /** 售出库存是**懒加载**：只有站在那个 tab 上才取（切回来会带着当前筛选重取） */
  useEffect(() => {
    if (tab === 'sold') void loadSold();
  }, [tab, loadSold]);

  useEffect(() => {
    void (async () => {
      try {
        setLocations(await locationsApi.list());
      } catch {
        setLocations([]);
      }
    })();
  }, []);

  const applyFilters = (patch: Partial<LedgerFilterInput>): void => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  const search = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    applyFilters({ keyword: keywordDraft.trim() });
  };

  const handleNotesSaved = useCallback((orderId: string, notes: string): void => {
    setOrders((current) =>
      current === null
        ? current
        : { ...current, items: current.items.map((item) => (item.orderId === orderId ? { ...item, notes } : item)) },
    );
  }, []);

  const exportCsv = async (): Promise<void> => {
    if (exporting) return;
    setExporting(true);
    setExportError(null);
    setNotice(null);
    try {
      const download = await ledgerApi.exportCsv(query);
      saveBlob(download.blob, download.filename);
      setNotice(`已导出 ${download.filename}`);
    } catch {
      // 失败一律用 UI 规范 §3.2 的逐字文案：blob 通道读不出错误体的 code（见 api/ledger.ts 文件头）
      setExportError('导出失败，请重试');
    } finally {
      setExporting(false);
    }
  };

  const openRefund = (next: LedgerOrderItem): void => {
    setRefundTarget(next);
    setRefundNonce((current) => current + 1);
  };

  /**
   * 退款成功（含同键重放）后的收尾。
   *
   * 金额一律用**服务端响应**里的 `amount`（UI 规范 §6.4 规则 1：部分退款是按行退的，
   * 前端从 `totalAmount − refundedAmount` 推不出本次退了多少钱）。
   * `replayed: true` 必须说另一句话：那是「这笔已经退过了」，不是「又退了一笔」。
   */
  const handleRefunded = useCallback(
    (result: OrderRefundResponse): void => {
      setRefundTarget(null);
      const amount = formatServerYuan(result.amount) ?? AMOUNT_UNAVAILABLE_TEXT;
      setNotice(result.replayed ? `退款已处理，未重复扣减：${amount}` : `已退款 ${amount}`);
      void load();
      if (tab === 'sold') void loadSold();
    },
    [load, loadSold, tab],
  );

  return (
    <div className="max-w-7xl" data-testid="ledger-page">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">账本</h1>
        <Button
          data-testid="ledger-export-csv"
          variant="secondary"
          size="sm"
          className="min-h-[44px]"
          onClick={() => void exportCsv()}
          disabled={exporting}
        >
          {exporting ? '导出中…' : '导出 CSV'}
        </Button>
      </div>

      {exportError !== null && (
        <p
          data-testid="ledger-export-error"
          className="mb-3 rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600"
        >
          {exportError}
        </p>
      )}
      {notice !== null && (
        <p data-testid="ledger-notice" className="mb-3 rounded-lg border-2 border-black bg-gray-50 p-2 text-sm">
          {notice}
        </p>
      )}

      <section className="mb-6 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-sm font-bold">地点</span>
          <select
            data-testid="filter-location"
            value={filters.locationId}
            onChange={(event) => applyFilters({ locationId: event.target.value })}
            className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
          >
            <option value="">全部地点</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm font-bold">开始日期</span>
          <input
            type="date"
            data-testid="filter-from"
            value={filters.fromDay}
            onChange={(event) => applyFilters({ fromDay: event.target.value })}
            className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm font-bold">结束日期</span>
          <input
            type="date"
            data-testid="filter-to"
            value={filters.toDay}
            onChange={(event) => applyFilters({ toDay: event.target.value })}
            className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm font-bold">状态</span>
          <select
            data-testid="filter-status"
            value={filters.status}
            onChange={(event) => applyFilters({ status: event.target.value })}
            className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
          >
            <option value="">全部状态</option>
            {ORDER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {ORDER_STATUS_TEXT[status]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm font-bold">退款状态</span>
          <select
            data-testid="filter-refund-status"
            value={filters.refundStatus}
            onChange={(event) => applyFilters({ refundStatus: event.target.value })}
            className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
          >
            <option value="">全部退款状态</option>
            {REFUND_STATUSES.map((status) => (
              <option key={status} value={status}>
                {REFUND_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>

        <form className="flex items-end gap-2" onSubmit={search}>
          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold">关键字</span>
            <input
              type="search"
              data-testid="filter-keyword"
              value={keywordDraft}
              maxLength={100}
              placeholder="订单号 / 备注 / 商品名"
              onChange={(event) => setKeywordDraft(event.target.value)}
              className="h-12 rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
            />
          </label>
          <Button data-testid="filter-search" type="submit" variant="secondary">
            搜索
          </Button>
        </form>
      </section>

      {summary !== null && <LedgerSummaryCards summary={summary} />}

      <div className="mb-4 flex gap-2">
        <button
          type="button"
          data-testid="ledger-tab-orders"
          aria-pressed={tab === 'orders'}
          onClick={() => setTab('orders')}
          className={tabClass(tab === 'orders')}
        >
          账本
        </button>
        <button
          type="button"
          data-testid="ledger-tab-sold"
          aria-pressed={tab === 'sold'}
          onClick={() => setTab('sold')}
          className={tabClass(tab === 'sold')}
        >
          售出库存
        </button>
      </div>

      {tab === 'sold' ? (
        <SoldInventoryTab items={sold} loading={soldLoading} error={soldError} />
      ) : (
        <>
          {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
          {!loading && error !== null && (
            <p className="font-bold text-red-600" data-testid="ledger-error">
              {error}
            </p>
          )}
          {!loading && error === null && orders !== null && orders.items.length === 0 && (
            <p className="text-gray-500">暂无账单记录</p>
          )}
          {!loading && error === null && orders !== null && orders.items.length > 0 && (
            <>
              {/* 无 `ledger:refund` ⇒ 不传 `onRefund` ⇒ 一行入口都不渲染（真正的拦截在服务端） */}
              <OrderLedgerTable
                orders={orders.items}
                onNotesSaved={handleNotesSaved}
                {...(canRefund ? { onRefund: openRefund } : {})}
              />
              <div className="mt-4 flex items-center gap-3">
                <Button
                  data-testid="ledger-prev"
                  size="sm"
                  variant="secondary"
                  disabled={orders.page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  上一页
                </Button>
                <span className="text-sm text-gray-600" data-testid="ledger-page-info">
                  第 {orders.page} / {orders.totalPages} 页 · 共 {orders.total} 单
                </span>
                <Button
                  data-testid="ledger-next"
                  size="sm"
                  variant="secondary"
                  disabled={orders.page >= orders.totalPages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  下一页
                </Button>
              </div>
            </>
          )}
        </>
      )}

      {/*
        `key` 里带 `refundNonce`：**重新打开 = 重新挂载**（`RefundDialog` 因此在挂载时生成新的
        幂等键，并把范围、数量、原因全部重置）。同一个 `key` 存活期间的重试复用同一把键
        ⇒ 网络超时后的重试会被服务端判成同键重放（200 + `replayed: true`），而不是退两次钱。
      */}
      {refundTarget !== null && canRefund && (
        <RefundDialog
          key={`${refundTarget.orderId}:${refundNonce}`}
          order={refundTarget}
          onClose={() => setRefundTarget(null)}
          onRefunded={handleRefunded}
        />
      )}
    </div>
  );
}
